import { useCallback, useEffect, useRef, useState } from 'react';
import {
  schedulingApi,
  scheduleApi,
  positionApi,
  breakTimeSettingsApi,
} from '../services/api';
import {
  BreakTimeRule,
  PayRecord,
  SchedulingBootstrapResponse,
  SchedulingPublicationStatus,
  SchedulingScheduleCount,
  SchedulingWeekViewResponse,
} from '../types';

let bootstrapCache: SchedulingBootstrapResponse | null = null;
let bootstrapPromise: Promise<SchedulingBootstrapResponse> | null = null;

export function clearSchedulingBootstrapCache() {
  bootstrapCache = null;
  bootstrapPromise = null;
}

async function loadBootstrapData(): Promise<SchedulingBootstrapResponse> {
  try {
    return await schedulingApi.getBootstrap();
  } catch {
    const [schedules, positions, breakTimeSettings] = await Promise.all([
      scheduleApi.getAll(),
      positionApi.getAll(),
      breakTimeSettingsApi.get().catch(() => ({ id: undefined, isPaid: false, rules: [] })),
    ]);
    return {
      schedules,
      positions,
      breakTimeSettings: breakTimeSettings || { id: undefined, isPaid: false, rules: [] },
    };
  }
}

function normalizeWeekView(data: SchedulingWeekViewResponse): SchedulingWeekViewResponse {
  return {
    ...data,
    publicationStatuses: normalizeNumericRecord<SchedulingPublicationStatus>(data.publicationStatuses),
    scheduleCounts: normalizeNumericRecord<SchedulingScheduleCount>(data.scheduleCounts),
    payRecords: normalizeNumericRecord<PayRecord>(data.payRecords),
    locationPayRecords: normalizeNumericRecord<PayRecord>(data.locationPayRecords),
  };
}

function normalizeNumericRecord<T>(raw: Record<string, T> | Record<number, T> | undefined): Record<number, T> {
  const out: Record<number, T> = {};
  if (!raw) return out;
  for (const [key, value] of Object.entries(raw)) {
    out[Number(key)] = value;
  }
  return out;
}

export function useSchedulingData(
  locationId: number | null,
  scheduleId: number | null,
  weekStartStr: string | null,
  weekEndStr: string | null
) {
  const [bootstrap, setBootstrap] = useState<SchedulingBootstrapResponse | null>(bootstrapCache);
  const [weekView, setWeekView] = useState<SchedulingWeekViewResponse | null>(null);
  const [loadingBootstrap, setLoadingBootstrap] = useState(!bootstrapCache);
  const [loadingWeekView, setLoadingWeekView] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const loadBootstrap = async () => {
      try {
        if (bootstrapCache) {
          setBootstrap(bootstrapCache);
          setLoadingBootstrap(false);
          return;
        }

        if (!bootstrapPromise) {
          bootstrapPromise = loadBootstrapData();
        }

        const data = await bootstrapPromise;
        bootstrapCache = data;
        bootstrapPromise = Promise.resolve(data);

        if (!cancelled) {
          setBootstrap(data);
          setLoadingBootstrap(false);
        }
      } catch {
        bootstrapPromise = null;
        if (!cancelled) {
          setError('Failed to load scheduling reference data');
          setLoadingBootstrap(false);
        }
      }
    };

    loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadWeekView = useCallback(async () => {
    if (!locationId || !scheduleId || !weekStartStr || !weekEndStr) {
      abortRef.current?.abort();
      setWeekView(null);
      setLoadingWeekView(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingWeekView(true);

    try {
      const data = await schedulingApi.getWeekView(
        {
          locationId,
          scheduleId,
          weekStart: weekStartStr,
          weekEnd: weekEndStr,
        },
        controller.signal
      );

      if (requestId !== requestIdRef.current) return;
      setWeekView(normalizeWeekView(data));
      setError(null);
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return;
      const isCanceled =
        typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        (err as { code?: string }).code === 'ERR_CANCELED';
      if (!isCanceled) {
        setError('Failed to load schedule week data');
        setWeekView(null);
        throw err;
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingWeekView(false);
      }
    }
  }, [locationId, scheduleId, weekStartStr, weekEndStr]);

  useEffect(() => {
    loadWeekView().catch(() => {
      // Error already stored in state; rethrow from reloadWeekView for callers that await it.
    });
  }, [loadWeekView]);

  const reloadWeekView = useCallback(async () => {
    await loadWeekView();
  }, [loadWeekView]);

  const reloadAll = useCallback(async () => {
    clearSchedulingBootstrapCache();
    setLoadingBootstrap(true);
    try {
      const data = await loadBootstrapData();
      bootstrapCache = data;
      bootstrapPromise = Promise.resolve(data);
      setBootstrap(data);
    } finally {
      setLoadingBootstrap(false);
    }
    await loadWeekView();
  }, [loadWeekView]);

  return {
    bootstrap,
    weekView,
    loadingBootstrap,
    loadingWeekView,
    error,
    reloadWeekView,
    reloadAll,
  };
}

export function breakTimeRulesFromSettings(
  breakTimeSettings: { rules?: BreakTimeRule[] } | null | undefined
): BreakTimeRule[] {
  if (breakTimeSettings?.rules && breakTimeSettings.rules.length > 0) {
    return breakTimeSettings.rules;
  }
  return [
    { minHours: 0, maxHours: 4.5, breakMinutes: 0 },
    { minHours: 4.5, maxHours: 6.5, breakMinutes: 15 },
    { minHours: 6.5, maxHours: 10, breakMinutes: 30 },
    { minHours: 10, maxHours: 24, breakMinutes: 45 },
  ];
}
